import { ReactNode, useEffect } from "react";
import { SidebarProvider, SidebarTrigger, SidebarInset } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/supabase-auth";
import { useLogout } from "@/hooks/useLogout";
import type { OrganizationData } from '@/types/common';
import { MfaWarningBanner } from "@/components/auth/MfaWarningBanner";

interface DashboardSidebarLayoutProps {
  children: ReactNode;
  sidebar: ReactNode;
  headerContext?: 'user' | 'organization' | 'admin';
  organizationData?: OrganizationData | null;
}

export function DashboardSidebarLayout({ 
  children, 
  sidebar, 
  headerContext = 'user',
  organizationData 
}: DashboardSidebarLayoutProps) {
  const { session, profile } = useAuth();
  const { logout } = useLogout();

  const handleLogout = () => {
    logout("/");
  };

  // Lock page scroll on dashboard layouts so only the main content area scrolls.
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;

    const prevRootOverflow = root.style.overflow;
    const prevBodyOverflow = body.style.overflow;

    root.style.overflow = "hidden";
    body.style.overflow = "hidden";

    return () => {
      root.style.overflow = prevRootOverflow;
      body.style.overflow = prevBodyOverflow;
    };
  }, []);

  const userInitials = profile?.full_name
    ? profile.full_name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : session?.user?.email?.[0]?.toUpperCase() || "U";

  return (
    <SidebarProvider defaultOpen={true}>
      <div className="h-svh min-h-svh flex w-full overflow-hidden">
        {sidebar}
        <SidebarInset className="flex-1 min-w-0 flex flex-col min-h-0">
          {/* Header */}
          <header className="sticky top-0 z-header flex h-header-height shrink-0 items-center gap-4 border-b bg-background px-4 lg:px-6">
            <SidebarTrigger className="-ml-1 lg:hidden" aria-label="Toggle navigation menu" />
            <div className="flex-1" />
              
            {/* Context-aware header display */}
            <div className="flex items-center gap-3">
              {headerContext === 'organization' && organizationData && (
                <Link 
                  to="/org/settings" 
                  className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                >
                  {organizationData.settings?.branding_settings?.logo_url && (
                    <Avatar className="h-9 w-9">
                      <AvatarImage 
                        src={organizationData.settings.branding_settings.logo_url} 
                        alt={`${organizationData.name} logo`}
                      />
                      <AvatarFallback>
                        {organizationData.name?.substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  )}
                  <span className="text-sm font-semibold hidden sm:inline">
                    {organizationData.name}
                  </span>
                </Link>
              )}

              {headerContext === 'admin' && (
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="font-semibold">
                    Platform Admin
                  </Badge>
                  <span className="text-sm hidden sm:inline">
                    {profile?.full_name || session?.user?.email?.split('@')[0] || 'Admin'}
                  </span>
                </div>
              )}
              
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                Log out
              </Button>
            </div>
          </header>

          {/* Main Content */}
          <main className="flex-1 min-h-0 overflow-auto" id="main-content" role="main" tabIndex={-1}>
            <div className="p-layout-content-padding">
              <MfaWarningBanner />
              {children}
            </div>
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}
